import { useState, useMemo } from 'react';
import { RefreshCw, Filter, MapPin, List, Zap, Truck, Car, Download } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { useLaadpleinConcurrenten } from '@/hooks/useLaadpleinConcurrenten';
import { useLaadpleinSyncStatus } from '@/hooks/useLaadpleinSyncStatus';
import { UnifiedMapComponent } from '@/hooks/useMapWithProvider';
import type { Concurrent } from '@/types/laadplein';

function getMarkerColor(site: any): string {
  const kw = site.available_power_kw ?? 0;
  if (kw >= 350) return '#dc2626'; // dark red - MCS/mega charger
  if (kw >= 150) return '#ef4444'; // red - truck HPC
  if (kw >= 50) return '#f97316';  // orange - fast DC
  return '#3b82f6';                // blue - standard
}

// Get unique sorted operators from data
function getOperators(data: Concurrent[]): string[] {
  const ops = new Set(data.map(c => c.operator).filter(Boolean));
  return Array.from(ops).sort();
}

export function ConcurrentieTab() {
  const { concurrenten, isLive, isLoading, refresh, refreshNL, spulaPostcode } = useLaadpleinConcurrenten();
  const { getStatus } = useLaadpleinSyncStatus();
  const ocmStatus = getStatus('OpenChargeMap');

  // View state
  const [view, setView] = useState<'map' | 'list'>('map');

  // Filter state
  const [minKw, setMinKw] = useState(0);
  const [typeFilter, setTypeFilter] = useState<'all' | 'truck' | 'auto'>('all');
  const [operatorFilter, setOperatorFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'operationeel' | 'niet operationeel'>('all');
  const [showFilters, setShowFilters] = useState(false);

  const operators = useMemo(() => getOperators(concurrenten), [concurrenten]);

  // Apply filters
  const filtered = useMemo(() => {
    return concurrenten.filter(c => {
      if (c.kw < minKw) return false;
      if (typeFilter === 'truck' && (c.truckLaders ?? 0) === 0) return false;
      if (typeFilter === 'auto' && (c.truckLaders ?? 0) > 0) return false;
      if (operatorFilter !== 'all' && c.operator !== operatorFilter) return false;
      if (statusFilter !== 'all' && c.status !== statusFilter) return false;
      return true;
    });
  }, [concurrenten, minKw, typeFilter, operatorFilter, statusFilter]);

  // Map sites from filtered data
  const mapSites = useMemo(() =>
    filtered
      .filter(c => c.latitude && c.longitude)
      .map((c, i) => ({
        id: c.id || `concurrent-${i}`,
        name: c.naam,
        address: `${c.plaats} - ${c.operator}`,
        latitude: c.latitude!,
        longitude: c.longitude!,
        available_power_kw: c.kw,
        development_size_chargers: c.punten,
        status: c.status,
        city: c.operator,
        detailUrl: c.ocmId ? `https://openchargemap.org/site/poi/details/${c.ocmId}` : null,
        displayOptions: { showName: true, showAddress: true, showPower: true, showChargers: true, showCity: true },
      })),
    [filtered]
  );

  // Stats
  const stats = useMemo(() => {
    const total = filtered.length;
    const truckHpc = filtered.filter(c => c.kw >= 150).length;
    const fastDc = filtered.filter(c => c.kw >= 50 && c.kw < 150).length;
    const operational = filtered.filter(c => c.status === 'operationeel').length;
    const uniqueOps = new Set(filtered.map(c => c.operator).filter(Boolean)).size;
    return { total, truckHpc, fastDc, operational, uniqueOps };
  }, [filtered]);

  const lastSync = ocmStatus.lastSync
    ? new Date(ocmStatus.lastSync).toLocaleDateString('nl-NL', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : null;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isLive ? 'bg-green-500' : 'bg-gray-400'}`} />
          <span className="text-sm text-gray-600">
            {isLive ? `${concurrenten.length} locaties geladen (OCM)` : 'Demo data'}
          </span>
          {lastSync && <span className="text-xs text-gray-400">Sync: {lastSync}</span>}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="default"
            size="sm"
            disabled={refreshNL.isPending}
            onClick={() => refreshNL.mutate({ min_power_kw: 50, max_results: 5000 })}
            className="flex items-center gap-1.5"
          >
            <Zap className={`w-3.5 h-3.5 ${refreshNL.isPending ? 'animate-pulse' : ''}`} />
            {refreshNL.isPending ? 'Laden heel NL...' : 'Laad heel NL'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={refresh.isPending}
            onClick={() => refresh.mutate({ latitude: 51.44, longitude: 5.47, radius_km: 50 })}
            className="flex items-center gap-1.5"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refresh.isPending ? 'animate-spin' : ''}`} />
            Regio Brabant
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-1.5"
          >
            <Filter className="w-3.5 h-3.5" />
            Filters
          </Button>
          <div className="flex border rounded-md">
            <Button variant={view === 'map' ? 'secondary' : 'ghost'} size="sm" onClick={() => setView('map')} className="rounded-r-none px-2">
              <MapPin className="w-4 h-4" />
            </Button>
            <Button variant={view === 'list' ? 'secondary' : 'ghost'} size="sm" onClick={() => setView('list')} className="rounded-l-none px-2">
              <List className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div className="flex flex-wrap gap-3 text-sm">
        <Badge variant="outline" className="gap-1"><MapPin className="w-3 h-3" /> {stats.total} locaties</Badge>
        <Badge variant="outline" className="gap-1 text-red-600 border-red-200"><Truck className="w-3 h-3" /> {stats.truckHpc} truck HPC</Badge>
        <Badge variant="outline" className="gap-1 text-orange-600 border-orange-200"><Zap className="w-3 h-3" /> {stats.fastDc} fast DC</Badge>
        <Badge variant="outline" className="gap-1 text-green-600 border-green-200">{stats.operational} operationeel</Badge>
        <Badge variant="outline" className="gap-1">{stats.uniqueOps} operators</Badge>
      </div>

      {/* Filter panel */}
      {showFilters && (
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Min. vermogen</label>
                <div className="flex items-center gap-3">
                  <Slider
                    value={[minKw]}
                    onValueChange={([v]) => setMinKw(v)}
                    min={0}
                    max={500}
                    step={25}
                    className="flex-1"
                  />
                  <span className="text-sm font-mono w-16 text-right">{minKw} kW</span>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Type</label>
                <Select value={typeFilter} onValueChange={(v: any) => setTypeFilter(v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle types</SelectItem>
                    <SelectItem value="truck">Alleen truck (&ge;150 kW)</SelectItem>
                    <SelectItem value="auto">Alleen auto (&lt;150 kW)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Operator</label>
                <Select value={operatorFilter} onValueChange={setOperatorFilter}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle operators</SelectItem>
                    {operators.map(op => (
                      <SelectItem key={op} value={op}>{op}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">Status</label>
                <Select value={statusFilter} onValueChange={(v: any) => setStatusFilter(v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle statussen</SelectItem>
                    <SelectItem value="operationeel">Operationeel</SelectItem>
                    <SelectItem value="niet operationeel">Niet operationeel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {(minKw > 0 || typeFilter !== 'all' || operatorFilter !== 'all' || statusFilter !== 'all') && (
              <div className="mt-3 flex items-center justify-between">
                <span className="text-xs text-gray-500">
                  {filtered.length} van {concurrenten.length} locaties getoond
                </span>
                <Button variant="ghost" size="sm" onClick={() => { setMinKw(0); setTypeFilter('all'); setOperatorFilter('all'); setStatusFilter('all'); }}>
                  Reset filters
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Map view */}
      {view === 'map' && (
        <Card>
          <CardContent className="p-0 h-[550px] rounded-lg overflow-hidden">
            {mapSites.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full gap-3 text-muted-foreground">
                <MapPin className="w-12 h-12 opacity-30" />
                <p>Geen laadpunten met locatie gevonden</p>
                <Button variant="default" size="sm" onClick={() => refreshNL.mutate({ min_power_kw: 50, max_results: 5000 })}>
                  <Zap className="w-4 h-4 mr-1" /> Laad alle NL laadpunten
                </Button>
              </div>
            ) : (
              <UnifiedMapComponent
                sites={mapSites}
                getMarkerColor={getMarkerColor}
                portalType="crm"
              />
            )}
          </CardContent>
          <div className="flex flex-wrap gap-3 px-4 py-2 text-xs text-gray-400 border-t">
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-red-800 rounded-full inline-block" />
              MCS (&ge;350 kW)
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-red-500 rounded-full inline-block" />
              Truck HPC (150-350 kW)
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-orange-500 rounded-full inline-block" />
              Fast DC (50-150 kW)
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-blue-500 rounded-full inline-block" />
              Standaard (&lt;50 kW)
            </span>
            <span className="ml-auto font-medium">{mapSites.length} op kaart</span>
          </div>
        </Card>
      )}

      {/* List view */}
      {view === 'list' && (
        <div className="space-y-1.5 max-h-[600px] overflow-y-auto">
          {filtered.map((c, i) => (
            <Card key={c.id || i}>
              <CardContent className="p-3 flex justify-between items-center">
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{c.naam}</div>
                  <div className="text-xs text-gray-500 truncate">
                    {c.plaats} &middot; {c.operator}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {(c.truckLaders ?? 0) > 0 && (
                    <Badge variant="outline" className="text-red-600 border-red-200 text-xs">
                      <Truck className="w-3 h-3 mr-0.5" />{c.truckLaders}
                    </Badge>
                  )}
                  {(c.autoLaders ?? 0) > 0 && (
                    <Badge variant="outline" className="text-blue-600 border-blue-200 text-xs">
                      <Car className="w-3 h-3 mr-0.5" />{c.autoLaders}
                    </Badge>
                  )}
                  <span className="text-sm font-semibold w-16 text-right">{c.kw} kW</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${
                    c.status === 'operationeel' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {c.status === 'operationeel' ? 'actief' : 'inactief'}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
          {filtered.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              Geen resultaten met huidige filters
            </div>
          )}
        </div>
      )}

      {/* SPULA section */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-medium text-gray-700">
              SPULA beschikbaarheid per postcodegebied
            </div>
            <span className="text-xs text-gray-400">Bron: RVO SPULA register (handmatig bijgewerkt)</span>
          </div>
          <div className="space-y-2">
            {spulaPostcode.map((sp) => (
              <div key={sp.pc} className="flex items-center gap-3">
                <span className="w-12 text-xs font-bold bg-gray-100 px-2 py-1 rounded text-center">
                  {sp.pc}
                </span>
                <span className="text-sm flex-1">{sp.regio}</span>
                <div className="flex gap-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div
                      key={i}
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                        i < sp.verleend
                          ? 'bg-red-100 text-red-600'
                          : i < sp.verleend + sp.behandeling
                          ? 'bg-yellow-100 text-yellow-600'
                          : 'bg-emerald-100 text-emerald-600'
                      }`}
                    >
                      {i < sp.verleend ? '\u00d7' : i < sp.verleend + sp.behandeling ? '?' : '\u2713'}
                    </div>
                  ))}
                </div>
                <span className="text-xs text-gray-400 w-20 text-right">{sp.resterend} van 5 vrij</span>
              </div>
            ))}
          </div>
          <div className="flex gap-3 mt-3 text-xs text-gray-400">
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-red-100 rounded-full inline-block border border-red-200" /> Verleend
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-yellow-100 rounded-full inline-block border border-yellow-200" /> In behandeling
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-emerald-100 rounded-full inline-block border border-emerald-200" /> Beschikbaar
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
